import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Copy, MoreVertical, ExternalLink, Ban, Cpu, UserCheck } from 'lucide-react';
import { Button } from '@/components/ui/button.jsx';
import { toast } from '@/components/ui/toaster.jsx';
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
} from '@/components/ui/dropdown-menu.jsx';
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogCancel,
  AlertDialogAction,
} from '@/components/ui/alert-dialog.jsx';
import StatusPill from './StatusPill.jsx';
import HandOutSheet from './HandOutSheet.jsx';
import { revokeToken } from '@/services/tap.service.js';

async function copyToClipboard(text, label) {
  try {
    await navigator.clipboard.writeText(text);
    toast.success(label || 'Copied');
  } catch {
    toast.error('Clipboard blocked — long-press to copy');
  }
}

// One token in the per-batch list. Dual layout: a dense single-line strip on
// desktop, a stacked card on mobile (mirrors TaskRow). Shared handlers; the
// HandOutSheet + revoke AlertDialog mount once per row.
export default function TokenRow({ token, onMutated }) {
  const navigate = useNavigate();
  const [expanded, setExpanded] = useState(false);
  const [handOutOpen, setHandOutOpen] = useState(false);
  const [revokeOpen, setRevokeOpen] = useState(false);
  const [revoking, setRevoking] = useState(false);

  const isRevoked = token.status === 'revoked';
  const canHandOut = !!token.programmed_at && !isRevoked;
  const shortId = token.id.slice(-8);

  async function doRevoke() {
    setRevoking(true);
    try {
      await revokeToken(token.id, 'revoked from TAP admin');
      toast.success('Token revoked');
      setRevokeOpen(false);
      onMutated?.();
    } catch (err) {
      toast.error(`Revoke failed: ${err.code || err.message}`);
    } finally {
      setRevoking(false);
    }
  }

  const idLabel = (
    <button
      type="button"
      onClick={() => setExpanded((v) => !v)}
      className="font-mono text-sm text-text hover:text-accent"
      title={token.id}
    >
      {expanded ? token.id : `…${shortId}`}
    </button>
  );

  const copyBtn = (
    <Button
      variant="ghost"
      size="sm"
      onClick={() => copyToClipboard(token.url, 'URL copied')}
      aria-label="Copy tag URL"
    >
      <Copy size={15} strokeWidth={1.5} /> Copy URL
    </Button>
  );

  const programBtn = (
    <Button
      variant="outline"
      size="sm"
      disabled={isRevoked}
      onClick={() => navigate(`/admin/tap/program/${token.id}`)}
    >
      <Cpu size={15} strokeWidth={1.5} /> Program
    </Button>
  );

  const handOutBtn = (
    <Button
      variant="outline"
      size="sm"
      disabled={!canHandOut}
      onClick={() => setHandOutOpen(true)}
      title={canHandOut ? undefined : 'Program the chip before handing it out'}
    >
      <UserCheck size={15} strokeWidth={1.5} /> {token.handed_out_at ? 'Edit hand-off' : 'Mark handed out'}
    </Button>
  );

  const overflow = (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" size="icon" aria-label="More actions">
          <MoreVertical size={18} strokeWidth={1.5} />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        <DropdownMenuItem onSelect={() => copyToClipboard(token.url, 'Full URL copied')}>
          <Copy size={15} strokeWidth={1.5} /> Copy full URL
        </DropdownMenuItem>
        <DropdownMenuItem onSelect={() => window.open(token.url, '_blank', 'noopener')}>
          <ExternalLink size={15} strokeWidth={1.5} /> View on /tap
        </DropdownMenuItem>
        {!isRevoked && (
          <>
            <DropdownMenuSeparator />
            <DropdownMenuItem
              className="text-accent focus:text-accent"
              onSelect={(e) => { e.preventDefault(); setRevokeOpen(true); }}
            >
              <Ban size={15} strokeWidth={1.5} /> Revoke
            </DropdownMenuItem>
          </>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );

  const handTo = token.handed_out_to ? (
    <span className="text-xs text-text-dim">→ {token.handed_out_to}</span>
  ) : null;

  return (
    <>
      {/* Desktop: single dense row */}
      <div className="hidden items-center gap-3 border-b border-border px-3 py-2 md:flex">
        <div className="flex w-44 items-center gap-2">{idLabel}</div>
        <StatusPill status={token.status} />
        {handTo}
        <div className="ml-auto flex items-center gap-1">
          {copyBtn}
          {programBtn}
          {handOutBtn}
          {overflow}
        </div>
      </div>

      {/* Mobile: stacked card */}
      <div className="border-b border-border px-3 py-3 md:hidden">
        <div className="flex items-center justify-between gap-2">
          <div className="flex items-center gap-2">{idLabel}<StatusPill status={token.status} /></div>
          {overflow}
        </div>
        {handTo && <div className="mt-1">{handTo}</div>}
        <div className="mt-3 grid grid-cols-3 gap-2">
          {copyBtn}
          {programBtn}
          {handOutBtn}
        </div>
      </div>

      <HandOutSheet open={handOutOpen} onOpenChange={setHandOutOpen} token={token} onDone={onMutated} />

      <AlertDialog open={revokeOpen} onOpenChange={setRevokeOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Revoke this keychain?</AlertDialogTitle>
            <AlertDialogDescription>
              …{shortId} will return the revoked state when tapped. This can't be undone from here.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={revoking}>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={doRevoke} disabled={revoking}>
              {revoking ? 'Revoking…' : 'Revoke'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
